import React, { type ComponentType, type FC } from "react";
import type { ToolCardProps } from "./types";
import { safeGet } from "./types";
import type { JSONValue } from "@/hooks/agent-stream";
import { ShimmerCard } from "./shimmer";
import { FallbackCard } from "./fallback-card";
import { JobListCard } from "./job-list-card";
import { JobDetailCard } from "./job-detail-card";
import { CVDetailCard } from "./cv-detail-card";
import { CVListCard } from "./cv-list-card";
import { CVPublishCard } from "./cv-publish-card";
import { ProfileSummaryCard } from "./profile-summary-card";
import { ProfileUpdateCard } from "./profile-update-card";
import { ApplicationPreviewCard } from "./application-preview-card";
import { ApplicationConfirmCard } from "./application-confirm-card";
import { ApplicationListCard } from "./application-list-card";
import { CandidateListCard } from "./candidate-list-card";
import { CandidateDetailCard } from "./candidate-detail-card";
import { CandidateActionCard } from "./candidate-action-card";
import { MessageDraftCard } from "./message-draft-card";
import { MessageConfirmCard } from "./message-confirm-card";
import { AssetCard } from "./asset-card";
import { CompanyUpdateCard } from "./company-update-card";
import { CompanyListCard } from "./company-list-card";
import { CvSectionPreviewCard } from "./cv-section-preview-card";
import { CVExportCard } from "./cv-export-card";
import { CVShareCard } from "./cv-share-card";
import { SubscriptionBlockedCard } from "./subscription-blocked-card";
import { SkillRunCard } from "./skill-run-card";
import { SkillListCard } from "./skill-list-card";
import { ConnectorListCard } from "./connector-list-card";
import { ConnectorActionCard } from "./connector-action-card";
import { NovaPlanCard } from "./nova-plan-card";
import { NovaListCard } from "./nova-list-card";
import { NovaActionCard } from "./nova-action-card";
import { MemoryCard } from "./memory-card";
import { MemoryActionCard } from "./memory-action-card";
import { ResearchCard } from "./research-card";
import { ArtifactCard } from "./artifact-card";
import {
  isArtifactCreatingTool,
  isArtifactEditCall,
  isSavedArtifactResult,
} from "../../artifacts/artifact-model";
import { ArtifactListCard } from "./artifact-list-card";
import { SitePublishCard } from "./site-publish-card";
import { ArtifactSuggestionsCard } from "./artifact-suggestions-card";
import { parseSuggestionsResult } from "../../artifacts/suggestions/suggestions-model";

export const SUBSCRIPTION_BLOCKED_TOOL_NAME = "__subscriptionBlocked";

/** Internal tools and workflows that should be invisible (no label, no card) */
export const HIDDEN_TOOLS = new Set([
  "updateWorkingMemory",
  "clearWorkingMemory",
  "getWorkingMemory",
  // Workflows: their individual tool calls already render cards
  "tailoredCvFlow",
  "seekerApplyFlow",
  "autoApplyQueueFlow",
  "catalogApplyFlow",
  "catalogAutoApplyQueueFlow",
  "documentIngestionFlow",
  "employerJobPostFlow",
  "candidateOutreachFlow",
  "talentPipelineFlow",
  // Skills: context-load + confirmation-prepare are internal plumbing.
  "skillsLoad",
  "skillPrepareRun",
  // Read-only CV comparison is narrated as bounded text, not a CV state card.
  "cvCompareWithMaster",
  // Nova: pause/resume are lightweight status toggles (no card).
  "novaPause",
  "novaResume",
  // Memory (M5.2): export + raw graph read are noisy plumbing (no card).
  "exportMemory",
  "memoryGraphGet",
]);

/** A tool narrated by the activity trace alone: no card once it succeeds. */
const ActivityLineOnly: FC<ToolCardProps> = () => null;

/**
 * An agent edit (plan 7.4): suggest mode is the `artifact-suggestions`
 * card; an applied edit is the file card at its new version.
 */
const ArtifactEditCard: FC<ToolCardProps> = (props) =>
  props.cardType === "artifact-suggestions" || parseSuggestionsResult(props.result) ? (
    <ArtifactSuggestionsCard {...props} />
  ) : (
    <ArtifactCard {...props} />
  );

const CARD_MAP: Record<string, ComponentType<ToolCardProps>> = {
  careerHighlightUpsert: FallbackCard,
  careerHighlightDelete: FallbackCard,
  profileSkillGroupsUpdate: FallbackCard,
  profileLanguagesUpdate: FallbackCard,
  profileJobPreferencesUpdate: FallbackCard,
  profilePrivateInfoUpdate: FallbackCard,
  profileCvConfigUpdate: FallbackCard,
  profileSectionsConfigUpdate: FallbackCard,
  profilePrivacyUpdate: FallbackCard,
  publicProfileBuilderGet: FallbackCard,
  publicProfileBlocksUpdate: FallbackCard,
  publicProfileSocialsUpdate: FallbackCard,
  publicProfileContactsUpdate: FallbackCard,
  publicProfileDesignUpdate: FallbackCard,
  publicProfileSettingsUpdate: FallbackCard,
  // Jobs
  jobSearch: JobListCard,
  jobRecommend: JobListCard,
  catalogJobSearch: JobListCard,
  jobGet: JobDetailCard,
  catalogJobGet: JobDetailCard,
  catalogSavedJobList: JobListCard,
  catalogJobSave: FallbackCard,
  catalogJobUnsave: FallbackCard,
  catalogJobHide: FallbackCard,
  catalogJobUnhide: FallbackCard,
  jobDraft: JobDetailCard,
  jobUpdate: JobDetailCard,
  jobPublish: JobDetailCard,
  jobClose: JobDetailCard,
  jobListByCompany: JobListCard,
  // CV
  cvCreate: CVDetailCard,
  cvUpdateSummary: CVDetailCard,
  cvTailor: CVDetailCard,
  cvDuplicate: CVDetailCard,
  cvSectionPreview: CvSectionPreviewCard,
  cvPublish: CVPublishCard,
  cvUnpublish: CVPublishCard,
  cvList: CVListCard,
  cvGet: CVDetailCard,
  cvUpdate: CVDetailCard,
  cvUpdateOverrides: CVDetailCard,
  cvSetFitToOnePage: CVDetailCard,
  cvGetResolved: CVDetailCard,
  cvDelete: ProfileUpdateCard,
  cvExport: CVExportCard,
  cvShareLinkCreate: CVShareCard,
  cvAccessGrantCreate: CVShareCard,
  // Profile
  profileGet: ProfileSummaryCard,
  profileUpdate: ProfileUpdateCard,
  experienceUpsert: ProfileUpdateCard,
  educationUpsert: ProfileUpdateCard,
  certificationUpsert: ProfileUpdateCard,
  projectUpsert: ProfileUpdateCard,
  serviceOfferUpsert: ProfileUpdateCard,
  experienceDelete: ProfileUpdateCard,
  educationDelete: ProfileUpdateCard,
  certificationDelete: ProfileUpdateCard,
  projectDelete: ProfileUpdateCard,
  serviceOfferDelete: ProfileUpdateCard,
  // Applications
  applicationPrepare: ApplicationPreviewCard,
  catalogApplyPrepare: ApplicationPreviewCard,
  applicationSubmit: ApplicationConfirmCard,
  catalogApplySubmit: ApplicationConfirmCard,
  applicationTrack: ApplicationListCard,
  catalogApplyTrack: ApplicationListCard,
  applicationGet: ApplicationConfirmCard,
  applicationListByJob: ApplicationListCard,
  applicationUpdateStatus: ApplicationConfirmCard,
  applicationWithdraw: ApplicationConfirmCard,
  // Candidates
  candidateSearch: CandidateListCard,
  candidateView: CandidateDetailCard,
  candidateShortlist: CandidateActionCard,
  // Messages
  messageDraft: MessageDraftCard,
  messageSend: MessageConfirmCard,
  messageScheduleInterview: MessageConfirmCard,
  conversationList: MessageDraftCard,
  conversationGet: MessageDraftCard,
  userSearch: FallbackCard,
  conversationCreate: FallbackCard,
  conversationSend: FallbackCard,
  conversationMarkRead: FallbackCard,
  conversationDelete: FallbackCard,
  messagingPreferencesGet: FallbackCard,
  messagingPreferencesUpdate: FallbackCard,
  blockedUserList: FallbackCard,
  userBlock: FallbackCard,
  userUnblock: FallbackCard,
  conversationMute: FallbackCard,
  conversationUnmute: FallbackCard,
  userReport: FallbackCard,
  notificationList: FallbackCard,
  notificationUnreadCount: FallbackCard,
  notificationPreferencesGet: FallbackCard,
  notificationMarkRead: FallbackCard,
  notificationMarkAllRead: FallbackCard,
  notificationPreferencesUpdate: FallbackCard,
  notificationDelete: FallbackCard,
  personalAnalyticsGet: FallbackCard,
  studioProjectList: FallbackCard,
  studioProjectCreate: FallbackCard,
  studioProjectGet: FallbackCard,
  studioProjectUpdate: FallbackCard,
  studioArtifactList: FallbackCard,
  studioArtifactGet: FallbackCard,
  studioArtifactGenerate: FallbackCard,
  studioArtifactUpdate: FallbackCard,
  studioArtifactRegenerate: FallbackCard,
  studioArtifactDelete: FallbackCard,
  studioApplyPrepare: FallbackCard,
  studioApplySubmit: FallbackCard,
  cvShareLinkList: FallbackCard,
  cvShareLinkUpdate: FallbackCard,
  cvShareLinkRevoke: FallbackCard,
  cvAccessGrantList: FallbackCard,
  cvAccessGrantUpdate: FallbackCard,
  cvAccessGrantRevoke: FallbackCard,
  cvSharedWithMeList: FallbackCard,
  cvSharedProfileGet: FallbackCard,
  cvSharedProfileUpdate: FallbackCard,
  cvSharedSectionUpsert: FallbackCard,
  cvSharedSectionDelete: FallbackCard,
  cvSharedStructuredUpdate: FallbackCard,
  cvHandoffDraft: FallbackCard,
  cvHandoffSend: FallbackCard,
  cvHandoffCancel: FallbackCard,
  // Assets
  assetAttach: AssetCard,
  assetRecall: AssetCard,
  // Company
  companyCreate: CompanyUpdateCard,
  companyList: CompanyListCard,
  companyGet: CompanyUpdateCard,
  companyUpdate: CompanyUpdateCard,
  // Skills
  skillInvoke: SkillRunCard,
  skillRun: SkillRunCard,
  skillsList: SkillListCard,
  skillCreate: SkillRunCard,
  skillPublish: SkillRunCard,
  // Connectors (M3.2 tools): reads → list card, writes → approval-gated action card
  listConnectedServices: ConnectorListCard,
  getRecentEmails: ConnectorListCard,
  getRecentSlackMessages: ConnectorListCard,
  sendEmail: ConnectorActionCard,
  postSlackMessage: ConnectorActionCard,
  triggerZapierWorkflow: ConnectorActionCard,
  triggerMakeScenario: ConnectorActionCard,
  // Nova (M4.3): compile → plan preview, list/history → list card, activate/run
  // → approval-gated action card. pause/resume are hidden (see HIDDEN_TOOLS).
  novaCompile: NovaPlanCard,
  novaList: NovaListCard,
  novaRunHistory: NovaListCard,
  novaActivate: NovaActionCard,
  novaRunNow: NovaActionCard,
  // Memory (M5.2): reads → recall/remember card, writes → approval-gated action
  // card. exportMemory + memoryGraphGet are hidden (see HIDDEN_TOOLS).
  recallMemory: MemoryCard,
  addMemory: MemoryCard,
  memoryNodeDelete: MemoryActionCard,
  memoryAccessRevoke: MemoryActionCard,
  forgetMemory: MemoryActionCard,
  // Research: the five read tools share one card. Sources render in the
  // Sources row under the answer, not in the card.
  webSearch: ResearchCard,
  readUrl: ResearchCard,
  readPdf: ResearchCard,
  readGithubRepo: ResearchCard,
  readSkill: ResearchCard,
  // Artifacts: files the agent creates, and the list of them.
  generateImage: ArtifactCard,
  createDocument: ArtifactCard,
  createPage: ArtifactCard,
  artifactGet: ArtifactCard,
  artifactList: ArtifactListCard,
  // Agent edits (Phase 2): the file card at its new version, or the
  // suggestions card in suggest mode. Reading a file is an activity line only.
  updatePage: ArtifactEditCard,
  updateDocument: ArtifactEditCard,
  editImage: ArtifactCard,
  artifactRead: ActivityLineOnly,
  // Publishing (Phase 2): the card opens the Publish sheet prefilled, or
  // manages the live site; unpublish shows the same card.
  publishArtifact: SitePublishCard,
  unpublishArtifact: SitePublishCard,
  // Internal: subscription block synthesized from a 403 response
  __subscriptionBlocked: SubscriptionBlockedCard,
};

export const ToolCardRenderer: FC<ToolCardProps> = (props) => {
  // Hidden internal tools → minimal indicator or nothing
  if (HIDDEN_TOOLS.has(props.toolName)) {
    return null;
  }

  // A file being made shows its own card from the moment it is called: an
  // image its placeholder at the requested ratio, a document, deck or page the
  // canvas card with the title the call named and "Preparing…". An agent
  // edit shows the file it edits, saying Editing. The same element stays
  // when the result lands.
  if (
    props.stage === "running" &&
    (isArtifactCreatingTool(props.toolName) ||
      isArtifactEditCall(props.toolName, props.args))
  ) {
    return <ArtifactCard {...props} />;
  }

  // Running → shimmer skeleton
  if (props.stage === "running") {
    return <ShimmerCard cardType={props.cardType} toolName={props.toolName} />;
  }

  // A tool call blocked by plan entitlement (e.g. a Plus/Pro-only skill)
  // reports as a normal completed result, not stage:'failed' - render the
  // same upgrade CTA as a subscription-blocked turn instead of that tool's
  // usual card, so the user gets a tappable button instead of bare prose.
  const entitlementError = safeGet<{ code?: string; decision?: JSONValue }>(
    props.result,
    "error",
  );
  if (entitlementError?.code === "SUBSCRIPTION_ENTITLEMENT_DENIED") {
    const blockedResult: JSONValue =
      entitlementError.decision === undefined
        ? {}
        : { decision: entitlementError.decision };
    const blockedProps: ToolCardProps = { ...props, result: blockedResult };
    return <SubscriptionBlockedCard {...blockedProps} />;
  }

  // Failed/error → fallback. A saved version is never a failure (a page
  // kept with findings the checks still flag shows its card).
  if (
    (props.stage === "failed" || props.isError) &&
    !isSavedArtifactResult(props.result)
  ) {
    return <FallbackCard {...props} />;
  }

  const Card = CARD_MAP[props.toolName] ?? FallbackCard;
  return <Card {...props} />;
};
